import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import cx from "classnames";
import { Link, useHistory, useParams, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import FontFaceObserver from "fontfaceobserver";
import LoadingBar, { hideLoading } from "react-redux-loading-bar";
import { Navbar, NavbarToggler, Collapse, Nav, NavItem, NavLink, Tooltip } from "reactstrap";

import { fetchDocumentDetails, updateDocumentReducer } from "../../store/actions/documentActions";
import { getResourceEntries } from "../../store/actions/resourcesActions";
import { fetchTemplateDetails } from "../../store/actions/templateActions";
import { Icon } from "../ui/icon";
import Modal from "../ui/modal";
import {
  alignSvg,
  displayAbstractShapes,
  prepareVideoHtml,
  stopAllResourceBuffering,
  getTemplateInfo,
  fileDownload,
  setResponsiveSrcToAllWidgets,
  detectDevice,
  joinFullName,
  decideIntegrationForProvider,
} from "../../_helpers/utils";

import global from "../../scss/dhp.scss";
import Free from "../../assets/images/ui-love.svg";
import Premium from "../../assets/images/ui-crown.svg";
import NotFound from "../public/NotFound";
import { useContextualUpgrade } from "../../hooks/useContextualUpgrade";
import { useCheckCompanyPlanInfo } from "../../hooks/useCheckCompanyPlanInfo";
import { COMPANY_SUPERADMIN } from "../../constants/company";
import ContextualUpgrade from "../Subscription/ContextualUpgrade";
import { useDocumentCreatePermission } from "../../hooks/useDocumentCreatePermission";
import Tour from "../User/Tour";
import { TR_PREVIEW } from "../../constants/user";
import { CompanyModal } from "../Company/CompanyModal";
import { DOCUMENT_PERMISSION_RO } from "../../constants/document";
import CanvasPageHtmlGenaretor from "../Editor/CanvasPageHtmlGenaretor";
import useDocumentParser from "../../hooks/useDocumentParser";
import ParserLoader from "./ParserLoader";
import {
  HYPERLINK_TYPE_BLOCK,
  HYPERLINK_TYPE_EMAIL,
  HYPERLINK_TYPE_PAGE,
  HYPERLINK_TYPE_SLIDE,
  HYPERLINK_TYPE_WEBSITE,
} from "../../constants/previewPresent";
import {
  fetchCompanyTemplateDetails,
  fetchIntegrations,
  fetchZohoCrmModulesSchema,
  getCompanyInfo,
  getCompanyPublicConfig,
  getConnectedAccounts,
} from "../../store/actions/companyActions";
import { getUserInfo } from "../../store/actions/authActions";
import { AppContext } from "../../contexts";
import { useSocket } from "../../hooks/useSocket";
import {
  EXTERNAL_STREAMING_FAILED,
  EXTERNAL_STREAMING_PROGRESS,
  EXTERNAL_STREAMING_START,
  EXTERNAL_STREAMING_SUCCESS,
} from "../../constants/socket";
import CircularProgressbar from "../ui/circularProgressbar";
import useCustomFonts from "../../hooks/useCustomFonts";
import useDeletedCustomFonts from "../../hooks/useDeletedCustomFonts";
import MapInfoTooltip from "./MapInfoTooltip";
import useVideo from "../../hooks/useVideo";
import { MAP, TYPE_PROPOSAL, VIDEO } from "../../constants/editor";
import { GET_COMPANY_PUBLIC_CONFIG_SUCCESS } from "../../store/actions/actionTypes";
import HTMLPlayer from "./HTMLPlayer";
import useAudioPlayer from "../../hooks/useAudioPlayer";
import PublishSignatureModal from "./PublishSignatureModal";
import userAvatar from "../../assets/images/ui-user.svg";
import useResponsivePreview from "../../hooks/useResponsivePreview";
import useEstimatePreview from "../../hooks/useEstimatePreview";
import PricingTableImageCaraousel from "../Editor/Modals/PricingTableImageCaraousel";
import useWidgetAutoAnimator from "../../hooks/useWidgetAutoAnimator";
import { useIntegrationGate } from "../../hooks/useIntegrationGate";
import IntegrationModal from "./IntegrationModal";
import useMergeFieldsHydration from "../../hooks/useMergeFieldsHydration";

let style = Object.assign({}, global);

const EllipsisTooltip = ({ text, id, tag: Tag = "div", className = "" }) => {
  const textRef = useRef();
  const [showTooltip, setShowTooltip] = useState(false);

  const toggleTooltip = () => setShowTooltip(!showTooltip && textRef.current.scrollWidth > textRef.current.offsetWidth);

  return (
    <>
      <Tag className={cx("text-truncate", className)} ref={textRef} id={id}>
        {text}
      </Tag>

      <Tooltip
        placement="bottom"
        isOpen={showTooltip}
        target={id}
        toggle={toggleTooltip}
        boundariesElement={document.getElementById("app")}>
        {text}
      </Tooltip>
    </>
  );
};

EllipsisTooltip.propTypes = {
  text: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  tag: PropTypes.string,
};

const SidebarMenu = ({ blockNodes, recordDetails, isOpen, setIsOpen }) => {
  const thumb = {
    src: recordDetails?.proposal?.owner?.image,
    loading: true,
    default: false,
  };
  const [userThumb, setUserThumb] = useState(thumb);

  // timeout holder
  const scrollTimeoutRef = useRef(null);

  //To Get Formatted Full Name
  const capitalize = str => (str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "");
  const fullName = [
    capitalize(recordDetails?.proposal?.owner?.firstname),
    capitalize(recordDetails?.proposal?.owner?.middlename),
    capitalize(recordDetails?.proposal?.owner?.lastname),
  ]
    .filter(Boolean)
    .join(" ");

  // To Handle Profile Picture
  const handleImageLoad = () => setUserThumb({ ...userThumb, loading: false });

  const handleImageError = () => setUserThumb({ ...userThumb, src: userAvatar, loading: false, default: true });

  useEffect(() => {
    const image = new Image();
    image.src = thumb.src;
    image.addEventListener("load", handleImageLoad);
    image.addEventListener("error", handleImageError);
    return () => {
      image.removeEventListener("load", handleImageLoad);
      image.removeEventListener("error", handleImageError);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div>
      <Navbar className={cx(style["p-0"], style["mr-3"], style["custom-tooltip"], style["menu-icon"])}>
        <NavbarToggler onClick={() => setIsOpen(prevState => !prevState)}>
          {isOpen ? <Icon icon="ui-close" /> : <Icon icon="menu4" />}
        </NavbarToggler>
        <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"], style["hide-on-mobile"])}>Menu</div>
      </Navbar>

      <Collapse
        isOpen={isOpen}
        className={cx(style["bg-light"], style["position-fixed"], style["menu-area"])}
        {...(isOpen ? { enter: false, exit: false } : {})}>
        <div className={cx(style["customScroll"], style["scroll-Y"])}>
          <Nav vertical className={cx(style["pl-md-3"], style["pt-2"])}>
            {blockNodes
              ?.filter(item => item.blockName && item.blockId)
              .map(item => (
                <NavItem key={item.blockId}>
                  <NavLink
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      const targetId = `section-${item?.blockId}`;
                      setIsOpen(false);
                      // Cancel any previous pending scroll
                      if (scrollTimeoutRef.current) {
                        clearTimeout(scrollTimeoutRef.current);
                      }
                      scrollTimeoutRef.current = setTimeout(() => {
                        const container = document.querySelector(".preview-content");
                        const section = document.getElementById(targetId);

                        if (!container || !section) return;

                        const containerRect = container.getBoundingClientRect();
                        const sectionRect = section.getBoundingClientRect();

                        const targetScrollTop = sectionRect.top - containerRect.top + container.scrollTop;

                        container.scrollTo({
                          top: targetScrollTop,
                          behavior: "smooth",
                        });
                      }, 200);
                    }}>
                    {item.blockName
                      .split(" ")
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(" ")}
                  </NavLink>
                </NavItem>
              ))}
          </Nav>
        </div>
        <div className={style["user-area"]}>
          <div
            className={cx(
              style["d-flex"],
              style["align-items-center"],
              style["pt-3"],
              style["border-top"],
              style["pb-2"]
            )}>
            <div>
              <figure className={cx(style["m-0"], style["user-logo"], style["rounded-circle"])}>
                {!userThumb.loading && (
                  <img
                    src={userThumb.src}
                    className={cx(style["img-fluid"], {
                      [style["default"]]: userThumb.default,
                    })}
                    alt="User image"
                  />
                )}
              </figure>
            </div>
            <div className={cx(style["ml-3"], style["user-info"])}>
              <EllipsisTooltip
                text={fullName}
                id="tooltip-username"
                tag="h6"
                className={cx(style["fw-7"], style["mb-0"], style["color-black"], style["text-truncate"])}
              />
              <EllipsisTooltip text={recordDetails?.proposal?.company} id="tooltip-company-name" />
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

SidebarMenu.propTypes = {
  blockNodes: PropTypes.array,
  recordDetails: PropTypes.object,
  isOpen: PropTypes.bool,
  setIsOpen: PropTypes.func,
};

const Preview = ({ versionSlug, versionData, ...props }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const webSocket = useSocket();
  const { start: initDocumentParser } = useDocumentParser();
  useCustomFonts(); // use active custom fonts
  useDeletedCustomFonts(); // use deleted custom fonts
  const { videoPlayer } = useVideo();

  const { updateActiveCompany } = useContext(AppContext);
  const getSlugRecordId = () => {
    if (versionData) return { slug: versionSlug, recordId: versionData?.id };
    else return useParams();
  };

  const myParam = useLocation().search;
  const currentRef = new URLSearchParams(myParam)?.get("ref");
  const routerLocation = useLocation();

  const { slug, recordId } = getSlugRecordId();
  const previewOverflow = useRef(null);
  const previewBox = useRef(null);
  const deviceWidthRef = useRef();
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalComponent, setModalComponent] = useState(false);
  const [alert, setAlert] = useState(false);
  const [showPreview, setShowPreview] = useState();
  const [docPagesMeta, setDocPagesMeta] = useState();
  const [downloadProgress, setDownloadProgress] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [isVideoPresent, setIsVideoPresent] = useState(false);
  const [muted, setMuted] = useState(true);
  const [showMapTooltip, setShowMapTooltip] = useState(false);
  const [isToggleMute, setIsToggleMute] = useState(false);
  const [isShowBadge, setIsShowBadge] = useState(false);
  const [isVideoTypeDocument, setIsVideoTypeDocument] = useState(false);
  const [showModalStatus, setShowModalStatus] = useState(false);
  const [isFullyAccepeted, setIsFullyAccepeted] = useState();
  const [acceptedSignerListId, setAcceptedSignerListId] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [isFading, setIsFading] = useState(true);
  // for responsive preview
  // const [deviceType, setDeviceType] = useState(detectDevice());
  // const [realDevice, setRealDevice] = useState(detectDevice());
  const [deviceType, setDeviceType] = useState("DESKTOP");
  const [realDevice, setRealDevice] = useState("DESKTOP");
  const [showAcceptTooltip, setshowAcceptTooltip] = useState(false);
  const isProposalLive = props?.published && props?.documentDetails?.proposal?.status === "live";
  const { showPricingTableImageCarousel, setShowPricingTableImageCarousel, widgetDelta, activeWidgetType } = useEstimatePreview({
    docPagesMeta,
    setDocPagesMeta,
    enable: isProposalLive,
    companyId: props.companyId,
  });

  const reloadParent = () => {
    if ([TYPE_PROPOSAL].includes(docPagesMeta?.documentType)) {
      setReloadKey(prev => prev + 1);
      setTimeout(() => {
        setIsFading(false);
      }, 300);
    }
  };

  const published = props?.published ?? false;

  useAudioPlayer({ docPagesMeta, muted, origin: "preview", active: !isVideoTypeDocument }); //audio player

  useWidgetAutoAnimator({ contextWidgets: docPagesMeta?.widgets, active: !isVideoTypeDocument }); // play widget animation

  const toggleMute = mute => {
    setIsToggleMute(true);
    setMuted(mute);
    document.querySelectorAll("video, audio").forEach(v => (v.muted = mute));
  };

  // useContextualUpgrade Hook to show update modal
  const showUpgrade = useContextualUpgrade();

  // Custom hook to check document create permission
  const [checkToDocumentCreate, isCreate] = useDocumentCreatePermission();

  // check is this company is paid or not
  const paidCompanyInfo = useCheckCompanyPlanInfo(true);

  const isCompSuperAdmin = useSelector(state => state.auth?.user?.company?.superadmin_company);
  const authState = useSelector(state => state?.auth);
  const fonts = useSelector(state => state?.resources?.fonts);
  const companyState = useSelector(state => state?.company);

  const connectedAccounts = useSelector(state => state.auth?.company?.connected_accounts);
  const { profilePermissionInfo = null, thirdPartyAccounts: { details: authorisedApps = [] } = {} } = useSelector(
    state => state?.company || {}
  );

  // ---------- Parse localStorage external_integration_data (pure + memoized) ----------
  const externalIntegrationFromLS = useMemo(() => {
    try {
      const v = JSON.parse(sessionStorage.getItem("external_integration_data"));
      return v || null;
    } catch (e) {
      return null;
    }
  }, []);

  const recordDetails = published
    ? props?.documentDetails
    : versionData
    ? versionData
    : slug === "documents" && !versionData
    ? useSelector(state => state?.document?.documentDetails)
    : slug === "templates"
    ? useSelector(state => state?.template?.templateDetails)
    : useSelector(state => state?.company?.companyTemplateInfo);

  const success = !versionData && recordDetails?.data?.id === recordId ? recordDetails?.success ?? true : true;

  const recordData = published
    ? recordDetails?.document?.rawdata
    : versionData
    ? recordDetails?.version?.rawdata
    : recordDetails?.data?.filedata
    ? JSON.parse(recordDetails?.data?.filedata)
    : recordDetails?.data?.version?.rawdata;

  const docName = published
    ? recordDetails?.name
    : slug === "documents"
    ? recordDetails?.data?.document_name
    : recordDetails?.data?.name;

  const docHtml = recordData?.contexts?.pageNodes;
  const docFonts = recordData?.metadata?.fonts;
  const dimension = recordData?.metadata?.dimension;
  const recordFullData = published
    ? { template_type: recordDetails?.type, version: { rawdata: recordDetails?.document?.rawdata } } // for Published
    : versionData
    ? recordDetails // for version
    : recordDetails?.data?.filedata
    ? {
        template_info: { type: recordDetails?.data?.type },
        version: { rawdata: JSON.parse(recordDetails?.data?.filedata) },
        data_source: recordDetails?.data?.data_source,
      } // for Template
    : recordDetails?.data; // for Document

  const templateIntegrationProvider = useMemo(() => recordFullData?.data_source?.provider, [recordFullData]);

  const providerRaw = useMemo(() => {
    if (externalIntegrationFromLS?.provider) return externalIntegrationFromLS.provider;
    if (templateIntegrationProvider) return templateIntegrationProvider;
    return null;
  }, [externalIntegrationFromLS, templateIntegrationProvider]);

  const { requestIntegrationForTemplate } = useIntegrationGate();
  const { ready } = useMergeFieldsHydration({ origin: published ? "public" : slug, companyId:props?.companyId });

  useEffect(() => {
    if ([TYPE_PROPOSAL].includes(docPagesMeta?.documentType)) {
      if (deviceType !== "DESKTOP" && sessionStorage.getItem("device_type") === "MOBILE") updateDeviceType("MOBILE");
      else if (deviceType !== "DESKTOP" && sessionStorage.getItem("device_type") === "TABLET")
        updateDeviceType("TABLET");
      else updateDeviceType("DESKTOP");
    }
  }, [docPagesMeta]);

  const updateDeviceType = device => {
    sessionStorage.setItem("device_type", device);
    // setDeviceType(device);
  };

  useResponsivePreview({
    source: "preview",
    documentType: docPagesMeta?.documentType,
    setMuted,
    deviceType,
    reloadParent,
    realDevice,
    published,
  });

  // useEffect(() => {
  //   if (published) {
  //     reloadParent();
  //     setTimeout(() => resizePreview(), 300);
  //   }
  // }, [realDevice, docPagesMeta?.documentType, published]);

  const invalidLinkModal = () => {
    return (
      <React.Fragment>
        <h4 className={cx(style["fw-7"], style["mb-4"])}>Invalid link</h4>

        <span
          aria-label="Close"
          className={cx(style["cross-modal"], style["rounded"])}
          onClick={() => setShowModal(false)}>
          <Icon icon="ui-close" />
        </span>

        <div>
          <div className={cx(style["delete-msg"], style["font-base"], style["color-33"], style["mb-4"])}>
            Sorry! The link is invalid
          </div>
        </div>
      </React.Fragment>
    );
  };

  const resizePreview = (_, firstRender) => {
    let currentDevice = detectDevice();
    // setRealDevice(currentDevice);
    if (published) updateDeviceType(currentDevice);
    if (deviceWidthRef?.current === window.innerWidth && deviceType !== currentDevice) return;
    deviceWidthRef.current = window.innerWidth;
    if (!previewOverflow.current && !previewBox.current) return;
    if ([TYPE_PROPOSAL].includes(docPagesMeta?.documentType) && ["MOBILE", "TABLET"].includes(deviceType)) {
      if (firstRender) previewBox.current.style.visibility = "hidden";
      return;
    }
    setIsFading(false);
    let screenWidth = window.innerWidth;
    let totalPages = docPagesMeta?.pageNodes?.length;
    let { height: docHeight, width: docWidth } = dimension;
    // below code applying on dhp-page-canvas width to auto to fix wrong page width
    if (document.getElementsByClassName("dhp-page-canvas")[0] && !props.preserveWidth)
      document.getElementsByClassName("dhp-page-canvas")[0].style.width = `auto`;

    let docLeftRightPadding = [TYPE_PROPOSAL].includes(docPagesMeta?.documentType) ? 0 : 120;
    docWidth = parseInt(docWidth) + docLeftRightPadding;

    if (docPagesMeta?.documentType === TYPE_PROPOSAL) {
      // --- Proposal Template Preview: Start ---
      (() => {
        const sections = document.querySelectorAll(".section");
        if (!sections.length) return;

        let negativeOffset = 0;

        sections.forEach((section, idx) => {
          if (idx === 0) return;
          section.style.marginTop = "-1px";
          section.style.overflow = "hidden";
          section.style.zIndex = "1";
          negativeOffset++;
        });

        const customScroll = document.querySelector(".customScroll");
        if (!customScroll || !previewOverflow.current || !previewBox.current) return;

        const previewPage = document.querySelector(".preview-page");

        const scale = screenWidth / parseInt(docWidth);

        requestAnimationFrame(() => {
          if(!previewOverflow?.current) return;
          Object.assign(previewOverflow.current.style, {
            height: `${parseInt(docHeight)}px`,
            width: `${parseInt(docWidth)}px`,
            transform: `scale(${scale})`,
          });

          // Box height needs reduction for the offset
          previewBox.current.style.height = `${parseInt(parseInt(docHeight) * scale - negativeOffset * scale)}px`;

          if (previewPage) {
            previewPage.style.margin = "0";
            previewPage.style.borderRadius = "0";
          }
        });
      })();
      // --- Proposal Template Preview: End ---
    } else {
      if (docWidth > screenWidth) {
        let scaledval = screenWidth / docWidth;
        let translateX = (screenWidth - (docWidth * scaledval - docLeftRightPadding)) / 2;
        previewOverflow.current.style.transform = `scale(${scaledval}) translateX(${translateX}px)`;
        previewBox.current.style.display = "flex";
        previewBox.current.style.height = `${
          (parseInt(docHeight) + 60) * scaledval * totalPages + (published ? 10 : 60) * scaledval
        }px`;
      } else {
        let translateX = (screenWidth - (docWidth - docLeftRightPadding)) / 2;
        previewOverflow.current.style.transform = `scale(1) translateX(${translateX}px)`;
        previewBox.current.style.display = "flex";
        previewBox.current.style.height = `${(parseInt(docHeight) + 60) * totalPages + (published ? 10 : 60)}px`;
      }
    }
    previewOverflow.current.style.willChange = "transform"; // resolved background line issue in preview
    // setTimeout(() => { // remove willChange after 300 ms
    //   previewOverflow.current.style.willChange = "";
    // }, 300)

    document.querySelectorAll(".dhp-widgets").forEach(wz => {
      wz.style.backfaceVisibility = "";
      wz.querySelectorAll("img").forEach(img => img.setAttribute("draggable", false));
    });
  };

  const loadGoogleFonts = async () => {
    if (!fonts?.length || !docFonts?.length) {
      setLoading(false);
      return;
    }

    const usedFonts = docFonts.map(name => name.toLowerCase());
    const filteredFonts = fonts.filter(f => usedFonts.includes(f.name.toLowerCase()));

    const promises = [];

    for (const font of filteredFonts) {
      let linkSelector = document.querySelector(`link[href="${font.value}"]`);

      if (!linkSelector) {
        const link = document.createElement("link");
        link.href = font.value;
        link.rel = "stylesheet";

        // Wait for stylesheet to finish loading before observing
        const linkPromise = new Promise((resolve, reject) => {
          link.onload = resolve;
          link.onerror = reject;
        });

        document.head.appendChild(link);
        await linkPromise; // ensure CSS loaded before FontFaceObserver
      }

      // Always add observers (even if link already existed)
      if (font.normal_weight)
        promises.push(new FontFaceObserver(font.name, { weight: font.normal_weight, style: "normal" }).load());
      if (font.bold_weight)
        promises.push(new FontFaceObserver(font.name, { weight: font.bold_weight, style: "normal" }).load());
      if (font.normal_italic_weight)
        promises.push(new FontFaceObserver(font.name, { weight: font.normal_italic_weight, style: "italic" }).load());
      if (font.bold_italic_weight)
        promises.push(new FontFaceObserver(font.name, { weight: font.bold_italic_weight, style: "italic" }).load());
    }

    try {
      await Promise.all(promises);
      setLoading(false);
    } catch (e) {
      console.error("Font loading failed:", e);
      setLoading(false);
      setAlert(!alert);
    }
  };

  const playAllVideos = () => {
    if (isVideoTypeDocument) return; 
    document.querySelectorAll("video")?.forEach(video => {
      // video.play();
      const widget = video.closest(".dhp-widgets");
      if (widget) videoPlayer({ event: "PLAY", widgetNode: widget, videoNode: video });
    });
  };

  const linkToWebsite = widget => {
    let hyperLinkURL = widget.getAttribute("data-hyperlink-url");
    hyperLinkURL = hyperLinkURL.match(/^https?:/) ? hyperLinkURL : "//" + hyperLinkURL;
    window.open(hyperLinkURL, "_blank");
  };

  const linkToEmail = widget => {
    window.location = "mailto:" + widget.getAttribute("data-hyperlink-url");
  };

  const linkToPage = widget => {
    let hyperLinkBlockId = widget.getAttribute("data-hyperlink-pageid");
    let targetPgeId = document.getElementById(`section-${hyperLinkBlockId}`);

    if (targetPgeId) {
      const container = document.querySelector(".preview-content");
      if (!container || !targetPgeId) return;
      const containerRect = container.getBoundingClientRect();
      const sectionRect = targetPgeId.getBoundingClientRect();
      const targetScrollTop = sectionRect.top - containerRect.top + container.scrollTop;
      container.scrollTo({
        top: targetScrollTop,
        behavior: "smooth",
      });
    }
    // if (targetPgeId) targetPgeId.scrollIntoView({ behavior: "smooth" });
    else {
      setModalComponent({
        name: invalidLinkModal,
      });
      setShowModal(!showModal);
    }
  };

  const onExit = () => {
    stopAllResourceBuffering(document, "video");
    if (props.location?.state?.redirectFromPublic) history.push("/templates");
    else {
      if (window.history.length > 1) {
        window.history.back();
        return false;
      } else window.close();
    }
  };

  const onReload = () => {
    location.reload();
  };

  const dynamicRouting = () => {
    stopAllResourceBuffering(document, "video");

    if (slug === "documents") {
      if (routerLocation.state?.fromSingleCollection) {
        history.push({
          pathname: `/editor/${recordId}`,
          state: { fromSingleCollectionPreview: true },
        });
      } else {
        history.push(`/editor/${recordId}`);
      }
      return;
    }

    if (slug === "templates" && paidCompanyInfo?.active_user_count) {
      const croppedThumb = recordDetails?.data?.thumb?.replace("/screenshot.png", "/screenshot-t.png") ?? "";
      if (
        recordDetails?.data?.premium &&
        paidCompanyInfo?.companyRole === COMPANY_SUPERADMIN &&
        !paidCompanyInfo?.isPaid
      ) {
        showUpgrade("premiumTemplate", croppedThumb);
        return;
      }

      const { stopCreating } = decideIntegrationForProvider({
        providerKey: providerRaw,
        isAdmin: ["Admin", "Super Admin"].includes(profilePermissionInfo?.name),
        connectedAccountsArr: connectedAccounts,
        authorisedAppsArr: authorisedApps,
      });

      const payload = getTemplateInfo({
        ...recordDetails?.data,
        thumb: (recordDetails?.data?.thumb || "").replace("/screenshot.png", "/screenshot-t.png"),
        ref: "dochipo-template",
      });

      if (!stopCreating) {
        checkToDocumentCreate();
        return;
      }

      // Ask central gate to handle modal/auth; it will call onAllowed if permitted
      requestIntegrationForTemplate({
        providerRaw: providerRaw,
        template: recordDetails?.data,
        isAdmin: ["Admin", "Super Admin"].includes(profilePermissionInfo?.name),
        connectedAccounts,
        authorisedApps,
        onAllowed: () => {
          history.push({
            pathname: "/documents/create",
            state: payload,
          });
        },
      });

      return;
    }

    if (slug === "company-templates") {
      const { stopCreating } = decideIntegrationForProvider({
        providerKey: providerRaw,
        isAdmin: ["Admin", "Super Admin"].includes(profilePermissionInfo?.name),
        connectedAccountsArr: connectedAccounts,
        authorisedAppsArr: authorisedApps,
      });

      const payload = getTemplateInfo({
        ...recordDetails?.data,
        thumb: (recordDetails?.data?.thumb || "").replace("/screenshot-t.png", "/screenshot-c.png"),
        ref: "company-template",
      });

      if (!stopCreating) {
        history.push({
          pathname: "/documents/create",
          state: payload,
        });
        return;
      }

      requestIntegrationForTemplate({
        providerRaw: providerRaw,
        template: recordDetails?.data,
        isAdmin: ["Admin", "Super Admin"].includes(profilePermissionInfo?.name),
        connectedAccounts,
        authorisedApps,
        onAllowed: () => {
          history.push({
            pathname: "/documents/create",
            state: payload,
          });
        },
      });
    }
  };

  // download as PDF from public page
  const handleDownload = () => {
    if (webSocket?.readyState === 1 && props?.companyId) {
      setDownloadProgress(true);

      const { proposal } = recordDetails;

      const subscribe = {
        type: EXTERNAL_STREAMING_START,
        companyId: props.companyId,
        userId: recordDetails?.owner,
        entryId: recordDetails.id,
        quality: "low",
        fileName: recordDetails.id,
        desiredFileName: `${docName.trim().replace(/[^a-zA-Z0-9 ]/g, "")}.pdf`,
        external: true,
        ...(published &&
          proposal && {
            documentName: docName.trim().replace(/[^a-zA-Z0-9 ]/g, ""),
            ownerName: joinFullName(proposal.owner.firstname, proposal.owner.middlename, proposal.owner.lastname),
            ownerEmail: proposal.owner.email,
            ownerTimeZone: proposal.owner.time_zone,
            ownerTimeFormat: proposal.owner.time_format,
            ownerDateFormat: proposal.owner.date_format,
            companyName: proposal.company,
          }),
      };
      webSocket.send(JSON.stringify(subscribe));
    }
  };

  //Function to show hide the signer modal
  const toggleModal = e => {
    if (!isFullyAccepeted) setShowModalStatus(!showModalStatus);
    else e.preventDefault();
  };

  //load company information if not
  useEffect(() => {
    if (!authState.company && authState?.user?.company?.id) {
      dispatch(getCompanyInfo(authState?.user?.company?.id));
      updateActiveCompany(authState?.user?.company);
    }
  }, [authState.user]);

  useEffect(() => {
    if (!authState?.user?.company?.id && !published) dispatch(getUserInfo());
    if (props?.companyId) dispatch(getCompanyPublicConfig(props?.companyId));
  }, []);

  // To Hide or Show the badge
  useEffect(() => {
    if (authState.type === GET_COMPANY_PUBLIC_CONFIG_SUCCESS && !companyState?.publicCompanyConfig?.hide_badge)
      setIsShowBadge(true);
  }, [companyState]);

  // Redirect to next action if document create permission ok
  useEffect(() => {
    if (isCreate) {
      history.push({
        pathname: "/documents/create",
        state: getTemplateInfo({
          ...recordDetails?.data,
          thumb: recordDetails?.data?.thumb?.replace("/screenshot.png", "/screenshot-c.png") ?? "",
        }),
      });
    }
  }, [isCreate]);

  useEffect(() => {
    if (docFonts && fonts && (recordDetails?.data?.id === recordId || versionData || published)) loadGoogleFonts();
  }, [recordFullData, fonts]);

  useEffect(() => {
    if (!docPagesMeta || !dimension || loading || (recordDetails?.data?.id !== recordId && !versionData)) return;

    setIsVideoPresent(document.querySelectorAll("video, audio").length > 0);
    resizePreview(null, true);
    prepareVideoHtml();
    if (!isToggleMute && !isVideoTypeDocument) playAllVideos();
    alignSvg();
    displayAbstractShapes();
    window.addEventListener("resize", resizePreview);
    return () => {
      window.removeEventListener("resize", resizePreview);
    };
  }, [loading, dimension, docPagesMeta, deviceType]);

  useEffect(() => {
    if (loading) return;

    document.querySelectorAll(`div[data-hyperlink-type='${HYPERLINK_TYPE_WEBSITE}']`).forEach(widget => {
      widget.addEventListener("click", () => linkToWebsite(widget));
    });

    document.querySelectorAll(`div[data-hyperlink-type=${HYPERLINK_TYPE_EMAIL}]`).forEach(widget => {
      widget.addEventListener("click", () => linkToEmail(widget));
    });

    document
      .querySelectorAll(
        `div[data-hyperlink-type=${HYPERLINK_TYPE_PAGE}], div[data-hyperlink-type=${HYPERLINK_TYPE_SLIDE}], div[data-hyperlink-type=${HYPERLINK_TYPE_BLOCK}]`
      )
      .forEach(widget => {
        widget.addEventListener("click", () => linkToPage(widget));
      });

    // For hide YouTube loader for version preview modal
    if (versionData) dispatch(hideLoading());

    return () => {
      document.querySelectorAll(`div[data-hyperlink-type=${HYPERLINK_TYPE_WEBSITE}]`).forEach(widget => {
        widget.removeEventListener("click", () => linkToWebsite(widget));
      });

      document.querySelectorAll(`div[data-hyperlink-type=${HYPERLINK_TYPE_EMAIL}]`).forEach(widget => {
        widget.removeEventListener("click", () => linkToEmail(widget));
      });

      document
        .querySelectorAll(
          `div[data-hyperlink-type=${HYPERLINK_TYPE_PAGE}], div[data-hyperlink-type=${HYPERLINK_TYPE_SLIDE}], div[data-hyperlink-type=${HYPERLINK_TYPE_BLOCK}]`
        )
        .forEach(widget => {
          widget.removeEventListener("click", () => linkToPage(widget));
        });
    };
  }, [docPagesMeta]);

  useEffect(() => {
    if (success) setShowPreview("document_success");
    else setShowPreview("document_not_found");
  }, [success]);

  useEffect(() => {
    if (!docPagesMeta && recordFullData?.version && !loading) {
      if (recordFullData?.version?.rawdata?.contexts?.pageNodes.length > 0) {
        setDocPagesMeta(recordFullData.version.rawdata.contexts);
        setIsVideoTypeDocument(
          Boolean(recordFullData.version.rawdata.contexts?.templateGroupName?.toLowerCase() === "video")
        );
      } else {
        initDocumentParser({ data: recordFullData }).then(meta => {
          setDocPagesMeta(meta);
          setIsVideoTypeDocument(Boolean(meta.templateGroupName?.toLowerCase() === "video"));
        });
      }
    }
  }, [recordDetails, loading]);

  useEffect(() => {
    if (published) {
      if (docPagesMeta?.pageNodes) document.getElementById("show-presentation-view")?.classList?.remove("disable");
      else document.getElementById("show-presentation-view")?.classList?.add("disable");
    }
  }, [docPagesMeta]);

  useEffect(() => {
    if (docPagesMeta?.widgets?.length > 0) {
      setShowMapTooltip(docPagesMeta.widgets.filter(wz => wz.data["data-asset-type"] === MAP)?.length > 0);
      setResponsiveSrcToAllWidgets({
        widgets: docPagesMeta.widgets,
        qualityPreference: "s3",
        parentSelector: `[data-selector="html-generator-preview"]`,
      });
    }
  }, [docPagesMeta?.widgets]);

  useEffect(() => {
    if (slug === "company-templates") dispatch(fetchCompanyTemplateDetails(recordId));
    if (slug === "documents") dispatch(updateDocumentReducer({ flashMessage: null }));
    if (currentRef === "editor" || (!versionData && !published)) {
      if (slug === "documents") dispatch(fetchDocumentDetails(recordId));
      if (slug === "templates") dispatch(fetchTemplateDetails(recordId, isCompSuperAdmin));
      setLoading(true);
    }
    if (currentRef === "info") dispatch(fetchDocumentDetails(recordId));

    if (!fonts) {
      setLoading(true);
      dispatch(getResourceEntries("fonts"));
    }
    // Add classes
    document.body.classList.add("preview-body");

    return () => {
      //  Remove classes
      document.body.classList.remove("preview-body");
    };
  }, []);

  useEffect(() => {
    if (webSocket?.readyState === 1) {
      webSocket.addEventListener("message", onMessage);
      return () => {
        webSocket.removeEventListener("message", onMessage);
      };
    }
  }, [webSocket]);

  const onMessage = useCallback(message => {
    const response = JSON.parse(message?.data);

    if (response.type === EXTERNAL_STREAMING_FAILED) setDownloadError(!downloadError);

    if (response?.progress && response.type === EXTERNAL_STREAMING_PROGRESS) setDownloadProgress(response?.progress);

    if (response?.downloadUrl && response.type === EXTERNAL_STREAMING_SUCCESS) {
      setTimeout(() => {
        fileDownload(response?.downloadUrl, `${docName.trim().replace(/[^a-zA-Z0-9 ]/g, "")}.pdf`);
        setDownloadProgress(false);
      }, 2000);
    }
    if (
      response.type === "dh:public:page:collaboration:after" &&
      response.companyId === props?.companyId &&
      response.documentId === recordDetails?.id
    ) {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    if (recordDetails?.type?.toLowerCase() === TYPE_PROPOSAL) {
      if (recordDetails.proposal.signers) {
        let acceptSignerList = [];
        setIsFullyAccepeted(true);

        // set status 1st time after load - add accepetd signer id to the list accepeted signer list
        recordDetails.proposal.signers.forEach(signer => {
          if (signer.status === "signed") acceptSignerList.push(signer._id);
        });
        setAcceptedSignerListId(acceptSignerList);

        // set status 1st time after load - the publish doc is fully or partial signed
        recordDetails.proposal.signers.some(signer => {
          if (signer.status !== "signed") setIsFullyAccepeted(false);
          return;
        });
      }
    }
  }, [recordDetails]);

  useEffect(() => {
    if (isProposalLive && webSocket?.readyState === 1) {
      webSocket.send(
        JSON.stringify({
          type: "dh:public:page:join",
          companyId: props?.companyId,
          documentId: recordDetails.id,
        })
      );
    }
    return () => {
      if (isProposalLive && webSocket?.readyState === 1) {
        webSocket.send(
          JSON.stringify({
            type: "dh:public:page:exit",
            companyId: props?.companyId,
            documentId: recordDetails.id,
          })
        );
      }
    };
  }, [props?.published, props?.documentDetails?.proposal?.status, webSocket]);

  useEffect(() => {
    if (
      docPagesMeta?.documentType &&
      !published &&
      !window.location.href.includes("/templates/") &&
      !window.location.href.includes("/company-templates/")
    ) {
      if ([TYPE_PROPOSAL].includes(docPagesMeta?.documentType)) {
        // for external source
        dispatch(fetchIntegrations());
      }
      dispatch(getConnectedAccounts());
    }
    if (
      [TYPE_PROPOSAL].includes(docPagesMeta?.documentType) &&
      (window.location.href.includes("/templates/") || window.location.href.includes("/company-templates/")) &&
      (connectedAccounts?.includes("zoho_crm") || providerRaw)
    ) {
      dispatch(fetchZohoCrmModulesSchema());
    }
  }, [docPagesMeta?.documentType, connectedAccounts]);

  useEffect(() => {
    if (isVideoTypeDocument) setMuted(false);
  }, [isVideoTypeDocument]);

  return (
    <>
      {!versionData && <LoadingBar style={{ zIndex: "100002", height: "2px", backgroundColor: "#e4504f" }} />}
      {!loading && showPreview === "document_success" && (
        <div>
          {/* Company updated checking */}
          {authState.user?.company_updated === false && (
            <Modal
              showModal={true}
              modalClassName="p-0 company-modal"
              className="modal-lg"
              component={CompanyModal}
              type="update"
              backdrop="static"
            />
          )}
          {/* For contextual upgrade modal */}
          <ContextualUpgrade />
          {!versionData && (
            <header className={cx(style["dhp-header"], style["preview-header"])}>
              <div
                className={style["header-left"]}
                style={{
                  width: alert || ([TYPE_PROPOSAL].includes(docPagesMeta?.documentType) && !published) ? "33%" : "50%",
                }}>
                {docPagesMeta?.documentType === TYPE_PROPOSAL &&
                  !recordDetails?.proposal?.hide_navigation &&
                  recordDetails?.proposal?.hide_navigation !== undefined &&
                  published &&
                  docPagesMeta?.blockNodes?.some(item => item.blockName) && (
                    <SidebarMenu
                      blockNodes={docPagesMeta?.blockNodes}
                      recordDetails={recordDetails}
                      isOpen={isMenuOpen}
                      setIsOpen={setIsMenuOpen}
                    />
                  )}
                <h3 className={cx(style["fw-7"], style["m-0"], style["text-truncate"])}>{docName}</h3>
              </div>

              {alert && (
                <>
                  <div className={style["header-center"]}>
                    <div className={cx(style["alert"], style["alert-danger"], style["alert-block"], style["d-none"])}>
                      Some fonts couldn&apos;t be loaded.{" "}
                      <span onClick={onReload} className={cx(style["cursor-pointer"], style["link-blue"])}>
                        Reload
                      </span>
                    </div>
                  </div>
                </>
              )}

              {[TYPE_PROPOSAL].includes(docPagesMeta?.documentType) && !published && false && (
                <>
                  <div className={cx(style["header-center"])}>
                    <ul
                      className={cx(
                        style["d-flex"],
                        style["align-items-center"],
                        style["justify-content-center"],
                        style["view-area"]
                      )}>
                      <li
                        className={cx(style["custom-tooltip"], style["rounded"], {
                          [style["active"]]: deviceType === "DESKTOP",
                        })}
                        onClick={() => deviceType !== "DESKTOP" && updateDeviceType("DESKTOP")}>
                        <Icon icon={`ui-desktop`} />
                        <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"])}>
                          Desktop
                        </div>
                      </li>
                      <li
                        className={cx(style["custom-tooltip"], style["rounded"], {
                          [style["active"]]: deviceType === "TABLET",
                        })}
                        onClick={() => deviceType !== "TABLET" && (setIsFading(true), updateDeviceType("TABLET"))}>
                        <Icon icon={`ui-tablet`} />
                        <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"])}>
                          Tablet
                        </div>
                      </li>
                      <li
                        className={cx(style["custom-tooltip"], style["rounded"], {
                          [style["active"]]: deviceType === "MOBILE",
                        })}
                        onClick={() => deviceType !== "MOBILE" && (setIsFading(true), updateDeviceType("MOBILE"))}>
                        <Icon icon={`ui-mobile`} />
                        <div className={cx(style["custom-tooltip-content"], style["bottom"], style["mt-5"])}>
                          Mobile
                        </div>
                      </li>
                    </ul>
                  </div>
                </>
              )}

              {published && (
                <div
                  className={cx(style["header-right"], {
                    [style["publish-accpet"]]: recordDetails?.proposal?.signers,
                  })}>
                  <ul
                    className={cx(style["d-flex"], style["align-items-center"])}
                    style={{ marginRight: ![TYPE_PROPOSAL].includes(recordDetails?.type.toLowerCase()) && !isVideoTypeDocument ? "3rem" : "" }}>
                    {isVideoPresent && !isVideoTypeDocument && (
                      <li className={style["pl-2"]}>
                        <span
                          className={cx(style["custom-tooltip"], style["rounded"], {
                            [style["toggle-mute"]]: !props?.documentDetails?.download,
                            [style["toggle-mute-download"]]: props?.documentDetails?.download,
                          })}
                          onClick={() => toggleMute(!muted)}>
                          <Icon icon={`ui-${muted ? "volume-mute" : "volume"}`} />
                          <div className={cx(style["custom-tooltip-content"], style["bottom"])}>
                            {muted ? "Unmute" : "Mute"}
                          </div>
                        </span>
                      </li>
                    )}
                    {(props?.documentDetails?.download || isFullyAccepeted) && !downloadProgress && (
                      <li className={style["pl-2"]}>
                        <span
                          className={cx(
                            style["custom-tooltip"],
                            style["rounded"]
                            //   {
                            //   [style["mr-5"]]:
                            //     !isVideoTypeDocument && ![TYPE_PROPOSAL].includes(docPagesMeta?.documentType),
                            // }
                          )}
                          onClick={handleDownload}>
                          <Icon icon="ui-download" />
                          <div className={cx(style["custom-tooltip-content"], style["bottom"])}>Download</div>
                        </span>
                      </li>
                    )}
                    {(props?.documentDetails?.download || isFullyAccepeted) && downloadProgress && (
                      <li className={style["pl-2"]}>
                        <span
                          className={cx(
                            style["custom-tooltip"],
                            style["position-relative"],
                            style["rounded"]
                            //   {
                            //   [style["mr-5"]]:
                            //     !isVideoTypeDocument && ![TYPE_PROPOSAL].includes(docPagesMeta?.documentType),
                            // }
                          )}
                          style={{ cursor: "progress", alignItems: "center", display: "flex" }}>
                          <CircularProgressbar progress={downloadProgress} />
                        </span>
                      </li>
                    )}

                    {recordDetails?.proposal?.signers && (
                      <li className={style["pl-2"]}>
                        <span
                          id="proposal-accept-button"
                          className={cx(style["btn"], style["btn-border"], style["btn-h36"], style["black"], {
                            [style["disabled"]]: isFullyAccepeted,
                          })}
                          onClick={e => toggleModal(e)}>
                          Accept
                          {isFullyAccepeted && (
                            <Tooltip
                              placement="bottom"
                              target="proposal-accept-button"
                              boundariesElement={document.getElementById("app")}
                              isOpen={showAcceptTooltip}
                              toggle={() => setshowAcceptTooltip(!showAcceptTooltip)}>
                              The document is already <br></br>accepted
                            </Tooltip>
                          )}
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {!published && (
                <div className={style["header-right"]}>
                  <ul className={cx(style["d-flex"], style["align-items-center"])}>
                    {isVideoPresent && !isVideoTypeDocument && (
                      <li className={style["pl-2"]}>
                        <span
                          id="toggleMute"
                          className={cx(style["edit-preview"], style["custom-tooltip"], style["rounded"])}
                          onClick={() => toggleMute(!muted)}>
                          <Icon icon={`ui-${muted ? "volume-mute" : "volume"}`} />
                          <div className={cx(style["custom-tooltip-content"], style["bottom"])}>
                            {muted ? "Unmute" : "Mute"}
                          </div>
                        </span>
                      </li>
                    )}
                    {slug === "templates" && (
                      <li>
                        <img
                          src={recordDetails?.data?.premium ? Premium : Free}
                          width="20"
                          className={style["mr-2"]}
                          alt=""
                        />{" "}
                        {recordDetails?.data?.premium ? "Premium" : "Free"}
                      </li>
                    )}
                    {recordDetails?.data?.permission !== DOCUMENT_PERMISSION_RO && (
                      <li className={style["pl-2"]}>
                        <span
                          className={cx(style["edit-preview"], style["custom-tooltip"], style["rounded"], {
                            [style["disable"]]: !docPagesMeta?.pageNodes || !authState?.user?.company?.id,
                          })}
                          onClick={() => dynamicRouting()}
                          id="usethistemplate">
                          <Icon icon={slug === "documents" ? "ui-pencil" : "ui-cursor"} />
                          <div className={cx(style["custom-tooltip-content"], style["bottom"])}>
                            {slug === "documents" ? "Go to Editor" : "Use this Template"}
                          </div>
                        </span>
                        {recordDetails?.data?.premium && <Tour targetId="usethistemplate" tourType={TR_PREVIEW} />}
                      </li>
                    )}
                    <li className={style["pl-2"]}>
                      <span
                        className={cx(style["close-preview"], style["custom-tooltip"], style["rounded"], {
                          [style["disable"]]: !docPagesMeta?.pageNodes,
                        })}
                        onClick={e => onExit(e)}>
                        <Icon icon="ui-close" />
                        <div className={cx(style["custom-tooltip-content"], style["bottom"])}>Close</div>
                      </span>
                    </li>
                  </ul>
                </div>
              )}
            </header>
          )}

          <div className={cx({ [style["menu-overlay"]]: isMenuOpen })}></div>
          <div
            className={cx(style["preview-content"], style["customScroll"], [style["scroll-Y"]])}
            style={{
              overflowY: !ready ? "hidden" : docPagesMeta?.pageNodes ? "" : "hidden",
              height: docPagesMeta?.pageNodes ? "" : "calc(100vh - 70px)"
            }}
            key={reloadKey}>
            {(!docPagesMeta || !docPagesMeta?.pageNodes || !ready) && <ParserLoader origin={"preview"} />}
            {/* {(!docPagesMeta || !docPagesMeta?.pageNodes) && <ParserLoader origin={"preview"} />} */}
            {!isVideoTypeDocument && (
              <div
                className={cx(style["preview-box"], { [style["fade"]]: isFading })}
                style={{ visibility: !ready ? "hidden" : "" }}
                id="preview-box"
                ref={previewBox}
                data-selector="html-generator-preview">
                {realDevice === "DESKTOP" &&
                [TYPE_PROPOSAL].includes(docPagesMeta?.documentType) &&
                ["MOBILE", "TABLET"].includes(deviceType) ? (
                  <div
                    className={cx({
                      [style["mobile-view"]]: deviceType === "MOBILE",
                      [style["tablet-view"]]: deviceType === "TABLET",
                    })}>
                    <div className={cx(style["customScroll"], style["scroll-Y"])}>
                      <div id="preview-overflow-wrap">
                        <div ref={previewOverflow} id="preview-overflow">
                          {docPagesMeta?.pageNodes?.map((pageMeta, i) => (
                            <CanvasPageHtmlGenaretor
                              figureWidth={parseInt(dimension?.width)}
                              key={`preview-page-${i}`}
                              id={`dhp-page-${i}`}
                              className={"preview-page"}
                              pageNode={pageMeta}
                              docPagesMeta={docPagesMeta}
                              isPreviewPresentMode={false}
                              isPublishedPage={published}
                              chartRenderingRef={`#dhp-page-${i}`}
                              source="preview"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div id="preview-overflow" ref={previewOverflow}>
                    {docPagesMeta?.pageNodes?.map((pageMeta, i) => (
                      <CanvasPageHtmlGenaretor
                        figureWidth={parseInt(dimension?.width)}
                        key={`preview-page-${i}`}
                        id={`dhp-page-${i}`}
                        className={"preview-page"}
                        pageNode={pageMeta}
                        docPagesMeta={docPagesMeta}
                        isPreviewPresentMode={false}
                        isPublishedPage={published}
                        chartRenderingRef={`#dhp-page-${i}`}
                        source="preview"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            {isVideoTypeDocument && (
              <HTMLPlayer
                docPagesMeta={docPagesMeta}
                dimension={dimension}
                origin={"preview"}
                type={slug}
                recordDetails={recordDetails}
                published={published}
                ready={ready}
              />
            )}

            {published &&
              docPagesMeta?.pageNodes &&
              !(!isShowBadge && (isVideoTypeDocument || [TYPE_PROPOSAL].includes(docPagesMeta?.documentType))) && (
                <div
                  className={cx(style["preview-footer"], style["pb-4"], style["pt-4"], {
                    [style["d-none"]]: !isShowBadge && isVideoTypeDocument,
                  })}>
                  {isShowBadge && (
                    <div>
                      <Link to={{ pathname: "https://www.dochipo.com/" }} target="_blank">
                        Made in{" "}
                        <span className={style["logo-text"]}>
                          <span className={style["logo-red"]}>D</span>
                          <span className={style["logo-yellow"]}>o</span>
                          <span className={style["logo-green"]}>c</span>Hipo
                        </span>
                      </Link>
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>
      )}
      {showModal && (
        <Modal
          showModal={showModal}
          setShowModal={setShowModal}
          component={modalComponent.name}
          modalClassName={modalComponent?.modalClassName}
          className={modalComponent?.className}
          module={modalComponent?.module}
          assetUrl={modalComponent?.assetUrl}
        />
      )}
      {showPreview === "document_not_found" && <NotFound />}
      {showMapTooltip && <MapInfoTooltip />}

      {docPagesMeta?.audios?.map((audio, idx) => (
        <audio id={`player-${audio.id}`} key={idx} src={audio.src} preload="auto" data-audio-id={audio.id}></audio>
      ))}

      {showModalStatus && (
        <Modal
          showModal={showModalStatus}
          setShowModal={setShowModalStatus}
          component={PublishSignatureModal}
          modalClassName={"signature-modal"}
          signerList={recordDetails?.proposal?.signers}
          documentId={recordDetails.id}
          isFullyAccepeted={isFullyAccepeted}
          setIsFullyAccepeted={setIsFullyAccepeted}
          acceptedSignerListId={acceptedSignerListId}
          setAcceptedSignerListId={setAcceptedSignerListId}
          handleDownload={handleDownload}
          widgetDelta={widgetDelta}
          enabledEditing={props?.published && props?.documentDetails?.proposal?.status === "live"}
          companyId={props.companyId}
        />
      )}

      {showPricingTableImageCarousel && (
        <Modal
          showModal={showPricingTableImageCarousel}
          setShowModal={setShowPricingTableImageCarousel}
          component={PricingTableImageCaraousel}
          modalClassName={"image-carousel-modal"}
          activeWidgetType={activeWidgetType}
        />
      )}

      <IntegrationModal />
    </>
  );
};

//Props type validation
Preview.propTypes = {
  versionSlug: PropTypes.string,
  versionData: PropTypes.object,
  published: PropTypes.bool,
  documentDetails: PropTypes.object,
  location: PropTypes.object,
  preserveWidth: PropTypes.bool,
  companyId: PropTypes.string,
  email: PropTypes.string,
};

export default Preview;
